import { useEffect, useMemo, useState } from "react";
import { SearchIcon } from "../../icons";
import GuideHint from "../GetStartedGuide/GuideHint";
import { formatCryptoValue } from "../../utils/format";
// import PercentChange from "../PercentChange";

const AssetRow = ({ asset, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect?.(asset)}
    className="flex h-[70px] w-full shrink-0 items-center justify-between rounded-[12px] bg-background-alt px-3 py-[15px] text-left custom-transition hover:bg-option-hover"
  >
    <div className="flex min-w-0 items-center gap-2.5">
      <div
        className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full"
        style={asset.iconBg ? { backgroundColor: asset.iconBg } : undefined}
      >
        <img
          src={asset.logo}
          alt={asset.currency_code}
          className="size-9 rounded-full object-cover"
        />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-[19.5px] text-text-primary">
          {asset.currency_code}
        </p>
        <p className="text-xs font-normal leading-[16.5px] text-text-secondary">
          {asset.title}
        </p>
      </div>
    </div>
    <div className="flex shrink-0 flex-col items-end">
      <p className="whitespace-nowrap text-sm font-medium leading-[19.5px] text-text-primary">
        {/* {asset.valueLabel} */}
        {/* {asset.balance} {asset.currency_code} */}
        <span>{formatCryptoValue(asset.balance, asset?.decimals || 8)}</span>
      </p>
      {/* <PercentChange value={asset.change24h} positive={asset.positive} /> */}
    </div>
  </button>
);

const SelectAssetStep = ({
  onAssetSelect,
  spotlightActive = false,
  guideHint = null,
  cryptoAccounts = [],
}) => {
  const [search, setSearch] = useState("");
  const [searchHistory, setSearchHistory] = useState([]);

  const filteredAssets = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return cryptoAccounts;

    return cryptoAccounts.filter(
      (asset) =>
        asset.currency_code.toLowerCase().includes(query) ||
        asset.title.toLowerCase().includes(query)
    );
  }, [search, cryptoAccounts]);

  useEffect(() => {
    const query = search.trim().toLowerCase();

    // Only save searches that have results
    if (!query || filteredAssets.length === 0) return;
    const matchedAsset = filteredAssets[0];

    setSearchHistory((prev) => {
      // Remove duplicates
      const updated = prev.filter(
        (item) => item.currency_code !== matchedAsset.currency_code
      );

      // Add newest search to the beginning and keep only the latest 3
      return [matchedAsset, ...updated].slice(0, 3);
    });
  }, [search, filteredAssets]);

  const trendingSection = (
    <div className="flex w-full flex-col gap-2">
      {/* <p className="shrink-0 text-sm font-semibold leading-6 text-text-primary">
        Trending Assets
      </p> */}

      <div className="flex max-h-[450px] flex-col gap-2 overflow-y-auto pr-1.5 custom-scrollbar">
        {filteredAssets.map((asset) => (
          <AssetRow key={asset.id} asset={asset} onSelect={onAssetSelect} />
        ))}
      </div>
    </div>
  );

  return (
    <div
      className={`flex flex-col gap-4 pb-2 px-4 pt-2 sm:h-full sm:min-h-0 sm:pt-4 ${
        spotlightActive ? "max-sm:shrink-0" : "h-full min-h-0"
      }`}
    >
      <h3 className="shrink-0 px-2 text-base font-semibold leading-7 text-text-primary sm:text-lg">
        Select Asset
      </h3>

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex h-10 w-full shrink-0 items-center gap-2 rounded-[10px] border border-divider bg-background-alt py-2.5 pl-4 pr-3">
          <SearchIcon />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search Asset..."
            className="w-full border-0 bg-transparent p-0 text-xs font-normal leading-normal text-text-primary placeholder:text-text-secondary outline-none focus:border-0 focus:outline-none focus:ring-0"
          />
        </div>

        {searchHistory.length ? 
          <div className="hidden sm:block">
            <p className="shrink-0 text-sm font-medium leading-7 text-text-primary">
              Search History
            </p>
            <div className="flex flex-wrap gap-2">
              {searchHistory.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => onAssetSelect?.(asset)}
                  className="flex items-center gap-2 rounded-[4px] bg-background-alt px-2 py-2"
                >
                  <img
                    src={asset.logo}
                    alt={asset.currency_code}
                    className="size-[16px] rounded-full object-cover"
                  />
                  <p className="text-xs font-medium text-text-primary">
                    {asset.currency_code}
                  </p>
                </button>
              ))}
            </div>
          </div>
          : ""
        }

        {spotlightActive ? (
          <div className="relative z-40 max-sm:shrink-0 max-sm:-mx-4 max-sm:rounded-t-[12px] max-sm:bg-background-surface max-sm:px-4 max-sm:pb-4 max-sm:pt-4 sm:flex sm:min-h-0 sm:flex-1 sm:flex-col sm:rounded-none sm:bg-transparent sm:p-0">
            {guideHint && (
              <div className="pointer-events-none absolute inset-x-4 bottom-full z-50 mb-2 sm:hidden">
                <div className="pointer-events-auto mx-auto w-[90%] max-w-[280px]">
                  <GuideHint
                    title={guideHint.title}
                    description={guideHint.description}
                    primaryAction={guideHint.primaryAction}
                    secondaryAction={guideHint.secondaryAction}
                    onSkip={guideHint.onSkip}
                    onDone={guideHint.onDone}
                    onBuyCrypto={guideHint.onBuyCrypto}
                  />
                </div>
              </div>
            )}
            {trendingSection}
          </div>
        ) : (
          trendingSection
        )}
      </div>
    </div>
  );
};

export default SelectAssetStep;
